// @spec NIS2-STR-038
'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Shield } from 'lucide-react';
import { useState } from 'react';
import { Toaster } from 'sonner';

import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AuditorModeBanner } from '@/features/auditor-access/components/AuditorModeBanner';
import { AuditorNIS2IncidentDetail } from '@/features/auditor-access/components/AuditorNIS2IncidentDetail';
import { AuditorNIS2Incidents } from '@/features/auditor-access/components/AuditorNIS2Incidents';
import { useAuditorStore } from '@/stores/auditor-store';

export const Route = createFileRoute('/auditor/access/$accessId/nis2/incidents')({
  component: AuditorNIS2IncidentsPage,
});

function AuditorNIS2IncidentsPage() {
  const { accessId } = Route.useParams();
  const navigate = useNavigate();

  const { t } = useTranslation('auditor-access');

  const isAuditorMode = useAuditorStore.use.isAuditorMode();
  const currentAccess = useAuditorStore.use.currentAccess();

  const [selectedIncidentId, setSelectedIncidentId] = useState<string | null>(null);

  if (!isAuditorMode || !currentAccess) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertTitle>{t('routes.accessRequired')}</AlertTitle>
            <AlertDescription>
              {t('routes.accessRequiredDescription')}
            </AlertDescription>
          </Alert>
          <Button variant="outline" className="mt-4" onClick={() => void navigate({ to: '/' })}>
            Go to Home
          </Button>
        </div>
      </div>
    );
  }

  if (currentAccess.accessId !== accessId) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertTitle>{t('routes.invalidAccess')}</AlertTitle>
            <AlertDescription>
              {t('routes.invalidAccessDescription')}
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  if (!currentAccess.accessScope.includes('nis2_compliance')) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertTitle>{t('routes.accessDenied')}</AlertTitle>
            <AlertDescription>
              Your auditor access does not include permission to view NIS2 compliance data.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  const handleIncidentClick = (incidentId: string) => {
    setSelectedIncidentId(incidentId);
  };

  const handleBackToList = () => {
    setSelectedIncidentId(null);
  };

  const handleBackToOverview = () => {
    void navigate({ to: `/auditor/access/${accessId}/nis2` });
  };

  return (
    <div className="min-h-screen bg-background">
      <AuditorModeBanner hasOtherOrganizations={false} />

      <div className="p-8">
        <div className="mx-auto max-w-6xl">
          {selectedIncidentId ? (
            <AuditorNIS2IncidentDetail
              accessId={accessId}
              incidentId={selectedIncidentId}
              onBack={handleBackToList}
            />
          ) : (
            <AuditorNIS2Incidents
              accessId={accessId}
              organizationName={currentAccess.name}
              onIncidentClick={handleIncidentClick}
              onBackToOverview={handleBackToOverview}
            />
          )}
        </div>
      </div>

      <Toaster position="top-right" richColors />
    </div>
  );
}
